"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultIssueAPI = exports.encodeIssueRequest = void 0;
const big_js_1 = __importDefault(require("big.js"));
const vaults_1 = require("./vaults");
const utils_1 = require("../utils");
const fee_1 = require("./fee");
const issueRedeem_1 = require("../utils/issueRedeem");
const transaction_1 = require("./transaction");
function encodeIssueRequest(req, network) {
    return utils_1.encodeParachainRequest(req, network);
}
exports.encodeIssueRequest = encodeIssueRequest;
class DefaultIssueAPI extends transaction_1.DefaultTransactionAPI {
    constructor(api, btcNetwork, electrsAPI, account) {
        super(api, account);
        this.btcNetwork = btcNetwork;
        this.electrsAPI = electrsAPI;
        this.vaultsAPI = new vaults_1.DefaultVaultsAPI(api, btcNetwork);
        this.feeAPI = new fee_1.DefaultFeeAPI(api);
    }
    getRequestLimits(vaults) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!vaults)
                vaults = yield this.vaultsAPI.getVaultsWithIssuableTokens();
            const vaultsArr = [...vaults.entries()];
            if (vaultsArr.length === 0) {
                return { singleVaultMaxIssuable: big_js_1.default(0), totalMaxIssuable: big_js_1.default(0) };
            }
            const singleVaultMaxIssuable = vaultsArr[0][1];
            const totalMaxIssuable = vaultsArr.reduce((total, [_, vaultAvailable]) => {
                return total.plus(vaultAvailable);
            }, new big_js_1.default(0));
            return { singleVaultMaxIssuable, totalMaxIssuable };
        });
    }
    /**
     * @param events The EventRecord array returned after sending an issue request transaction
     * @returns The issueId associated with the request. If the EventRecord array does not
     * contain issue request events, the function throws an error.
     */
    getIssueIdsFromEvents(events) {
        return issueRedeem_1.getRequestIdsFromEvents(events, this.api.events.issue.RequestIssue, this.api);
    }
    request(amount, atomic = true, retries = 0, cachedVaults) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const availableVaults = cachedVaults || (yield this.vaultsAPI.getVaultsWithIssuableTokens());
                const amountsPerVault = issueRedeem_1.allocateAmountsToVaults(availableVaults, amount);
                const result = yield this.requestAdvanced(amountsPerVault, atomic);
                const successfulSum = result.reduce((sum, req) => sum.plus(req.issueRequest.amount.toString()), new big_js_1.default(0));
                const remainder = amount.sub(successfulSum);
                if (remainder.eq(0) || retries === 0)
                    return result;
                else {
                    return (yield this.request(remainder, atomic, retries - 1, availableVaults)).concat(result);
                }
            }
            catch (e) {
                return Promise.reject(e.message);
            }
        });
    }
    requestAdvanced(amountsPerVault, atomic) {
        return __awaiter(this, void 0, void 0, function* () {
            const txs = new Array();
            for (const [vault, amount] of amountsPerVault) {
                const griefingCollateral = yield this.getGriefingCollateral(amount);
                // mul() here is a hacky workaround for rounding errors
                const griefingCollateralPlanck = new big_js_1.default(utils_1.dotToPlanck(griefingCollateral.toString()) || "0").add(100);
                const griefingCollateralCompact = this.api.createType("Compact<Collateral>", griefingCollateralPlanck.toString());
                const amountWrapped = this.api.createType("Compact<Wrapped>", utils_1.btcToSat(amount.toString()));
                txs.push(this.api.tx.issue.requestIssue(amountWrapped, vault, griefingCollateralCompact));
            }
            // batchAll fails atomically, batch allows partial successes
            const batch = (atomic ? this.api.tx.utility.batchAll : this.api.tx.utility.batch)(txs);
            try {
                const result = yield this.sendLogged(batch, this.api.events.issue.RequestIssue);
                const ids = this.getIssueIdsFromEvents(result.events);
                const issueRequests = yield this.getRequestsByIds(ids);
                return ids.map((issueId, idx) => ({ id: issueId, issueRequest: issueRequests[idx] }));
            }
            catch (e) {
                return Promise.reject(e);
            }
        });
    }
    execute(requestId, btcTxId, merkleProof, rawTx) {
        return __awaiter(this, void 0, void 0, function* () {
            const parsedRequestId = this.api.createType("H256", requestId);
            [merkleProof, rawTx] = yield utils_1.getTxProof(this.electrsAPI, btcTxId, merkleProof, rawTx);
            const executeIssueTx = this.api.tx.issue.executeIssue(parsedRequestId, merkleProof, rawTx);
            yield this.sendLogged(executeIssueTx, this.api.events.issue.ExecuteIssue);
        });
    }
    cancel(issueId) {
        return __awaiter(this, void 0, void 0, function* () {
            const cancelIssueTx = this.api.tx.issue.cancelIssue(issueId);
            yield this.sendLogged(cancelIssueTx, this.api.events.issue.CancelIssue);
        });
    }
    setIssuePeriod(blocks) {
        return __awaiter(this, void 0, void 0, function* () {
            const period = this.api.createType("BlockNumber", blocks);
            const tx = this.api.tx.sudo
                .sudo(this.api.tx.issue.setIssuePeriod(period));
            yield this.sendLogged(tx);
        });
    }
    getIssuePeriod() {
        return __awaiter(this, void 0, void 0, function* () {
            const blockNumber = yield this.api.query.issue.issuePeriod();
            return blockNumber.toNumber();
        });
    }
    list() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const issueRequests = yield this.api.query.issue.issueRequests.entriesAt(head);
            return issueRequests.map((v) => v[1]).map((req) => encodeIssueRequest(req, this.btcNetwork));
        });
    }
    mapForUser(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const issueRequestPairs = yield this.api.rpc.issue.getIssueRequests(account);
            const mapForUser = new Map();
            issueRequestPairs.forEach((issueRequestPair) => mapForUser.set(issueRequestPair[0], encodeIssueRequest(issueRequestPair[1], this.btcNetwork)));
            return mapForUser;
        });
    }
    getGriefingCollateral(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const griefingCollateralRate = yield this.feeAPI.getIssueGriefingCollateralRate();
            return yield this.feeAPI.getGriefingCollateral(amount, griefingCollateralRate);
        });
    }
    getFeesToPay(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const feePercentage = yield this.getFeeRate();
            const feeBtc = amount.mul(feePercentage);
            return new big_js_1.default(utils_1.roundUpBtcToNearestSatoshi(feeBtc.toString()));
        });
    }
    /**
     * @returns The fee charged for issuing. For instance, "0.005" stands for 0.5%
     */
    getFeeRate() {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            const issueFee = yield this.api.query.fee.issueFee.at(head);
            // TODO: return Big from decodeFixedPointType
            return new big_js_1.default(utils_1.decodeFixedPointType(issueFee));
        });
    }
    getRequestById(issueId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getRequestsByIds([issueId]))[0];
        });
    }
    getRequestsByIds(issueIds) {
        return __awaiter(this, void 0, void 0, function* () {
            const head = yield this.api.rpc.chain.getFinalizedHead();
            return Promise.all(issueIds.map((issueId) => __awaiter(this, void 0, void 0, function* () { return encodeIssueRequest(yield this.api.query.issue.issueRequests.at(head, issueId), this.btcNetwork); })));
        });
    }
}
exports.DefaultIssueAPI = DefaultIssueAPI;
//# sourceMappingURL=issue.js.map